import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Mail, Pencil, Trash2, Power, PowerOff, ShieldCheck } from 'lucide-react';
import { KVKK, dt } from './storeShared';
import { maskTcNo } from '../../lib/helpers';

const MAIL = { sent: ['Gönderildi', 'text-emerald-700'], failed: ['Hata', 'text-rose-600'], pending: ['Gönderiliyor…', 'text-sky-600'], unknown: ['Sonuç yok', 'text-slate-400'] };

// Son giriş e-postasının gönderim sonucu (e-posta geçmişinden)
const MailState = ({ m, id }) => {
  if (!m) return null;
  const [label, cls] = MAIL[m.status] || MAIL.unknown;
  return <span className={`font-medium ${cls}`} title={m.error || (m.status === 'unknown' ? 'E-posta geçmişinde kayıt bulunamadı (şablon kapalı olabilir)' : '')} data-testid={`store-mail-state-${id}`}>· {label}</span>;
};

const Account = ({ s }) => {
  if (s.account === 'silindi') {
    const d = s.account_deleted || {};
    return (
      <span className="text-xs" data-testid={`store-account-deleted-${s.id}`}>
        <span className="font-semibold text-rose-600">Silindi</span>
        <span className="block max-w-[15rem] leading-snug text-slate-500">Hesap Kullanıcı Yönetimi'nden silindi ({dt(d.at)}{d.by ? `, ${d.by}` : ''}). Yeniden göndermek için zarf simgesini kullanın.</span>
        {d.last_sent_at && <span className="block text-slate-400">Son gönderim: {dt(d.last_sent_at)}</span>}
      </span>
    );
  }
  if (!s.account) return <span className="text-xs text-slate-400" title="Yetkili e-postası girilmemiş veya giriş bilgisi hiç gönderilmemiş">Hesap yok</span>;
  return (
    <span className="text-xs">
      <span className={s.account === 'aktif' ? 'font-medium text-emerald-700' : 'font-medium text-amber-700'}>{s.account === 'aktif' ? 'Aktif' : 'Şifre belirlemedi'}</span>
      {s.credentials_sent_at && <span className="block text-slate-400">Mail: {dt(s.credentials_sent_at)} <MailState m={s.mail_state} id={s.id} /></span>}
    </span>
  );
};

export const StoreRow = ({ s, checked, onCheck, onEdit, onMail, onToggleClosed, onDelete, onKvkk }) => {
  const [label, cls] = KVKK[s.kvkk_state] || KVKK.yok;
  const name = `${s.contact_name || ''} ${s.contact_surname || ''}`.trim();
  return (
    <tr className={`border-t text-sm transition-colors hover:bg-slate-50 ${s.store_closed ? 'opacity-55' : ''}`} data-testid={`store-row-${s.id}`}>
      <td className="px-3 py-2.5"><Checkbox checked={checked} onCheckedChange={onCheck} data-testid={`store-check-${s.id}`} /></td>
      <td className="px-3 py-2.5">
        <span className="font-semibold text-slate-900">{s.name}</span>
        {s.store_closed && <Badge variant="outline" className="ml-2 border-rose-200 text-rose-600">Kapalı</Badge>}
        <span className="block text-xs text-slate-500">{s.location_name}{s.floor ? ` · ${s.floor}` : ''}</span>
      </td>
      <td className="px-3 py-2.5">{name || <span className="text-xs text-slate-400">—</span>}</td>
      <td className="px-3 py-2.5 font-mono text-xs" data-testid={`store-tc-${s.id}`}>{s.contact_tc_kimlik_no ? maskTcNo(s.contact_tc_kimlik_no) : '—'}</td>
      <td className="px-3 py-2.5 text-xs">{s.contact_phone || '—'}</td>
      <td className="px-3 py-2.5 text-xs">{s.contact_email || <span className="text-slate-400">—</span>}</td>
      <td className="px-3 py-2.5">
        <button type="button" disabled={s.kvkk_state === 'yok'} onClick={onKvkk} className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls} ${s.kvkk_state === 'yok' ? 'cursor-default' : 'hover:opacity-80'}`} data-testid={`store-kvkk-${s.id}`}>
          {s.kvkk_state === 'beklemede' && <span className="mr-1 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500 align-middle" />}{label}
        </button>
        <span className="block text-[11px] text-slate-400" data-testid={`store-quota-${s.id}`}>{s.kvkk_state === 'onay' ? <span className="inline-flex items-center gap-0.5 text-emerald-600"><ShieldCheck className="h-3 w-3" />Sınırsız</span> : `Talep: ${s.quota_used}/${s.quota_limit}`}</span>
      </td>
      <td className="px-3 py-2.5"><Account s={s} /></td>
      <td className="px-3 py-2.5">
        <div className="flex justify-end gap-0.5">
          <Button size="icon" variant="ghost" className="h-8 w-8 text-sky-600" title="Giriş bilgisi e-postası gönder" disabled={s.store_closed} onClick={onMail} data-testid={`store-mail-${s.id}`}><Mail className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-blue-600" title="Düzenle" onClick={onEdit} data-testid={`store-edit-${s.id}`}><Pencil className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-600" title={s.store_closed ? 'Yeniden aç' : 'Kapalı olarak işaretle (pasif)'} onClick={onToggleClosed} data-testid={`store-toggle-${s.id}`}>{s.store_closed ? <Power className="h-4 w-4" /> : <PowerOff className="h-4 w-4" />}</Button>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" title="Sil" onClick={onDelete} data-testid={`store-delete-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </td>
    </tr>
  );
};
